import { SITE_CONTACT_EMAIL } from '@/lib/site-contact';
import {
    absoluteUrl,
    SITE_DESCRIPTION,
    SITE_KEYWORDS,
    SITE_NAME,
    siteUrl,
} from '@/lib/site-url';

export { SITE_DESCRIPTION, SITE_KEYWORDS };

/** Site-wide fallback when an article has no cover or body image. */
export const DEFAULT_OG_IMAGE_PATH = '/images/og-default.jpg';
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

export function defaultOgImageUrl(): string {
    return absoluteUrl(DEFAULT_OG_IMAGE_PATH);
}

/** Prefer article OG crop; otherwise the site default share image. */
export function articleOgImageUrl(coverOgUrl?: string | null): string {
    const trimmed = coverOgUrl?.trim();
    return trimmed || defaultOgImageUrl();
}

export function publisherLogoUrl(): string {
    return absoluteUrl('/icon.png');
}

export interface SeoHeadOptions {
    title: string;
    description: string;
    path: string;
    /** Absolute URL override for canonical / og:url (cross-domain or custom). */
    canonicalUrl?: string;
    type?: 'website' | 'article' | 'profile';
    publishedTime?: string;
    modifiedTime?: string;
    image?: string;
    /** Defaults to 1200×630. */
    imageWidth?: number;
    imageHeight?: number;
    /** Defaults to the site name when omitted. */
    imageAlt?: string;
    keywords?: string;
    authors?: string[];
    section?: string;
    jsonLd?: Record<string, unknown> | Record<string, unknown>[];
    noIndex?: boolean;
}

export type BreadcrumbItem = {
    name: string;
    path: string;
};

export function seoTitle(title: string): string {
    if (title.includes(SITE_NAME)) return title;
    return `${title} | ${SITE_NAME}`;
}

/** Admin console head - noindex with a consistent "ניהול -" prefix. */
export function adminHead(title: string, path: string, description?: string) {
    return seoHead({
        title: `ניהול - ${title}`,
        description: description ?? `ממשק הניהול של ${SITE_NAME}.`,
        path,
        noIndex: true,
    });
}

export function seoHead(opts: SeoHeadOptions) {
    const url = opts.canonicalUrl?.trim() || absoluteUrl(opts.path);
    const fullTitle = seoTitle(opts.title);
    const robots = opts.noIndex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    const image = opts.image?.trim() || defaultOgImageUrl();
    const imageWidth = opts.imageWidth ?? OG_IMAGE_WIDTH;
    const imageHeight = opts.imageHeight ?? OG_IMAGE_HEIGHT;
    const imageAlt = opts.imageAlt?.trim() || SITE_NAME;

    const meta: Record<string, string>[] = [
        { title: fullTitle },
        { name: 'description', content: opts.description },
        { name: 'robots', content: robots },
        { name: 'googlebot', content: robots },
        { name: 'language', content: 'Hebrew' },
        { name: 'geo.region', content: 'IL' },
        { name: 'geo.placename', content: 'Ashkelon' },
        { name: 'geo.position', content: '31.6688;34.5743' },
        { name: 'ICBM', content: '31.6688, 34.5743' },
        { property: 'og:locale', content: 'he_IL' },
        { property: 'og:type', content: opts.type ?? 'website' },
        { property: 'og:site_name', content: SITE_NAME },
        { property: 'og:title', content: fullTitle },
        { property: 'og:description', content: opts.description },
        { property: 'og:url', content: url },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: '@Ashkelon_News' },
        { name: 'twitter:title', content: fullTitle },
        { name: 'twitter:description', content: opts.description },
    ];

    if (opts.keywords) {
        meta.push({ name: 'keywords', content: opts.keywords });
    } else if (!opts.noIndex) {
        meta.push({ name: 'keywords', content: SITE_KEYWORDS });
    }
    meta.push({ property: 'og:image', content: image });
    meta.push({ property: 'og:image:type', content: 'image/jpeg' });
    meta.push({ property: 'og:image:width', content: String(imageWidth) });
    meta.push({ property: 'og:image:height', content: String(imageHeight) });
    meta.push({ property: 'og:image:alt', content: imageAlt });
    meta.push({ name: 'twitter:image', content: image });
    meta.push({ name: 'twitter:image:alt', content: imageAlt });
    if (opts.publishedTime) {
        meta.push({ property: 'article:published_time', content: opts.publishedTime });
    }
    if (opts.modifiedTime) {
        meta.push({ property: 'article:modified_time', content: opts.modifiedTime });
    }
    if (opts.section) {
        meta.push({ property: 'article:section', content: opts.section });
    }
    if (opts.authors?.length) {
        for (const author of opts.authors) {
            meta.push({ property: 'article:author', content: author });
        }
    }

    const links = [
        { rel: 'canonical', href: url },
        { rel: 'alternate', href: url, hrefLang: 'he-IL' },
        { rel: 'alternate', href: url, hrefLang: 'he' },
        { rel: 'alternate', href: url, hrefLang: 'x-default' },
    ];

    const scripts: { type: string; children: string }[] = [];
    if (opts.jsonLd) {
        const items = Array.isArray(opts.jsonLd) ? opts.jsonLd : [opts.jsonLd];
        for (const item of items) {
            scripts.push({
                type: 'application/ld+json',
                children: JSON.stringify(item).replace(/</g, '\\u003c'),
            });
        }
    }

    return { meta, links, scripts };
}

/**
 * Site-wide head that is safe on every URL: JSON-LD, RSS, describedby.
 * Do not put canonical / og:url / title here - child routes merge heads, and a
 * homepage canonical on an article page fails Lighthouse SEO.
 */
export function seoSiteChrome() {
    return {
        meta: [
            { name: 'language', content: 'Hebrew' },
            { name: 'geo.region', content: 'IL' },
            { name: 'geo.placename', content: 'Ashkelon' },
            { name: 'geo.position', content: '31.6688;34.5743' },
            { name: 'ICBM', content: '31.6688, 34.5743' },
        ],
        links: [
            { rel: 'describedby', href: absoluteUrl('/llms.txt') },
            {
                rel: 'alternate',
                type: 'application/rss+xml',
                title: `${SITE_NAME} RSS`,
                href: absoluteUrl('/rss.xml'),
            },
        ],
        scripts: [organizationJsonLd(), websiteJsonLd()].map((item) => ({
            type: 'application/ld+json',
            children: JSON.stringify(item).replace(/</g, '\\u003c'),
        })),
    };
}

export function organizationJsonLd() {
    const origin = siteUrl();
    return {
        '@context': 'https://schema.org',
        '@type': 'NewsMediaOrganization',
        '@id': `${origin}/#organization`,
        name: SITE_NAME,
        alternateName: [
            'Ashkelon News',
            'אשקלון ניוז',
            'חדשות אשקלון',
            'אשקלון חדשות',
        ],
        url: origin,
        description: SITE_DESCRIPTION,
        foundingDate: '2006',
        inLanguage: 'he-IL',
        logo: {
            '@type': 'ImageObject',
            url: publisherLogoUrl(),
            width: 512,
            height: 512,
        },
        image: defaultOgImageUrl(),
        areaServed: {
            '@type': 'City',
            name: 'אשקלון',
            alternateName: 'Ashkelon',
            containedInPlace: {
                '@type': 'Country',
                name: 'Israel',
            },
        },
        address: {
            '@type': 'PostalAddress',
            addressLocality: 'Ashkelon',
            addressCountry: 'IL',
        },
        publishingPrinciples: absoluteUrl('/about'),
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'editorial',
            email: SITE_CONTACT_EMAIL,
            availableLanguage: ['Hebrew', 'he'],
            areaServed: 'IL',
        },
        sameAs: [
            'https://www.facebook.com/AshkelonNews/',
            'https://x.com/Ashkelon_News',
            'https://www.instagram.com/ashkelon_news/',
            'https://t.me/ashkelon_news',
        ],
    };
}

export function websiteJsonLd() {
    const origin = siteUrl();
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        name: SITE_NAME,
        alternateName: ['Ashkelon News', 'חדשות אשקלון', 'אשקלון חדשות'],
        url: origin,
        description: SITE_DESCRIPTION,
        inLanguage: 'he-IL',
        publisher: { '@id': `${origin}/#organization` },
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${origin}/search?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
        },
    };
}

export function breadcrumbJsonLd(items: BreadcrumbItem[]) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: absoluteUrl(item.path),
        })),
    };
}
